/**
 * The page-level action model, as far as it can be enforced in plain JavaScript.
 *
 * Actions reach a records page as data rather than as slot content, for four reasons that outlive
 * any one page:
 *
 * - **Scope is a required field.** Nothing can require a field on the contents of a slot.
 * - **Budgets count controls.** A component cannot count what is inside an opaque slot.
 * - **The count belongs in the label.** The filtered total and the selection size live in the
 *   table, not at the call site, so the label has to be built where the counts are.
 * - **Scope decides the container.** A selection-scoped Create renders in the selection bar rather
 *   than the create slot, and only an action held as data can be moved.
 *
 * The model asks one question of every action: after it, what is different?
 *
 * The primary control is the collection's primary action, which is usually creating. A collection
 * nothing is created in can have another: a billing worklist exists to be exported and marked
 * exported, and that is an act on the whole set rather than an extract, because the records are
 * different afterwards. The caller names it, since the question is what the records are for.
 *
 * | What changed | Kind | Where it renders |
 * | --- | --- | --- |
 * | There are more records | `create` | The primary control, or its menu |
 * | Existing records are different | `act` | The selection bar |
 * | Nothing, and records went out | `extract` | The selection bar, after a separator |
 * | Only my screen is different | `view` | The toolbar |
 * | Future records have a different shape | `configure` | The header |
 */

/** Controls, not actions: a create control with a ranked menu is one decision however many paths. */
export const ACTION_BUDGETS = Object.freeze({
  create: 1,
  act: 4,
  view: 4,
  configure: 1,
});

const SCOPES = ['selection', 'set'];

const isFilled = (value) => typeof value === 'string' && value.length > 0;

/**
 * Warns rather than throws. A budget overrun is a finding about the page, not a reason to refuse to
 * render it, and a page that stopped drawing mid-review would teach nobody anything.
 *
 * @param {string} message - What is wrong, in the reviewer's terms.
 */
function reportFinding(message) {
  if (process.env.NODE_ENV === 'production') return;
  // eslint-disable-next-line no-console
  console.warn(`[records] ${message}`);
}

/**
 * @typedef {Object} RecordAction
 * @property {string} key - Identifies the action; also the render key.
 * @property {string|((count: number|null) => string)} label - A scoped action takes the function
 *   form so its count lands inside the translated string rather than after it. A primary action
 *   that takes the records is handed null while there is no count it could keep to, and names
 *   itself without one.
 * @property {'act'|'extract'} [kind] - Required on a selection-bar action.
 * @property {'selection'|'set'} [scope] - Required on any action that can take one. A `selection`
 *   act waits for rows to be checked and renders in the selection bar. A `set` act is about the
 *   collection rather than anything picked out of it, so it is in the header from the start, in a
 *   menu of its own: rare, mostly administrative, and deliberately the least prominent control
 *   there so it never competes with the one that makes records.
 * @property {boolean} [expandedOnly] - Not a realistic phone workflow, so it leaves the create
 *   menu on a phone rather than being offered there and failing. Picking a spreadsheet off a device
 *   is the case this exists for; adding a folder is not. A claim about the hardware, not about the
 *   width: a narrow desktop window still offers it.
 * @property {RecordAction[]} [paths] - Creation paths under a named group, inside a create menu.
 *   An entry carrying this is a group: it runs nothing itself and needs no handler. One level
 *   only, because a second would be a navigation tree rather than a list of ways to make a record.
 * @property {boolean} [destructive]
 * @property {boolean} [disable]
 * @property {string} [unavailableReason] - Why the create action cannot be taken right now. The
 *   button stays reachable so its tooltip can say so; on a phone the FAB falls back to disabled.
 * @property {boolean} [loading] - What the control opens is still being read. Waiting rather than
 *   disabled, which would say the reader may not do this at all.
 * @property {string} [menuLabel] - The primary action's name where it leads a menu beside its
 *   siblings, which on a phone it does: "Add report template" above a list of presets does not say
 *   what sets it apart from them, and "Blank template" does.
 * @property {string} [menuHeading] - What the create menu's paths have in common, as a label above
 *   them. A label rather than a group, so it costs no press to reach them.
 * @property {string} [ariaLabel] - The accessible name, for a label that leans on a heading screen
 *   readers do not announce with each item.
 * @property {string} [icon] - On a primary action, what its FAB shows in place of the "+" that
 *   means adding. The header button stays text-only.
 * @property {string} [dataE2e]
 * @property {boolean} [takesRecords] - A primary action whose subject is the records on screen,
 *   rather than one that makes more. It gets the locks the table's own export has: nothing while a
 *   stored view is still arriving, and one run at a time, its handler awaited. Opt-in, because a
 *   create control that waited on a restore would be a fault.
 *   With rows checked it takes those instead, stays in the header through selection, and counts
 *   them in its label: the same control either way, so the reader never has to find it again.
 * @property {(acted?: { rows: Object[], columns: Object[] }) => void|Promise<void>} handler - A
 *   primary action is handed the records on screen and the columns chosen, which are the table's to
 *   know. Everything else ignores the argument.
 */

/**
 * Resolves an action's label against the number of records it would touch.
 *
 * @param {RecordAction} action
 * @param {number} count
 * @returns {string}
 */
export function actionLabel(action, count) {
  return typeof action.label === 'function' ? action.label(count) : action.label;
}

/**
 * @param {RecordAction} action
 * @returns {boolean} Whether the action carries enough to render and to be reviewed.
 */
export function isRenderableAction(action) {
  return !!action
    && isFilled(action.key)
    && (isFilled(action.label) || typeof action.label === 'function')
    && typeof action.handler === 'function';
}

/**
 * A create-menu entry that names a group rather than running something. It stands in for its
 * children, so it needs a label and at least one child worth showing, and no handler.
 *
 * @param {RecordAction} entry
 * @returns {boolean}
 */
export function isPathGroup(entry) {
  return Array.isArray(entry?.paths);
}

/**
 * @param {RecordAction} entry
 * @returns {boolean} Whether a create-menu entry carries enough to render, group or leaf.
 */
export function isRenderableCreatePath(entry) {
  if (!isPathGroup(entry)) {
    return isRenderableAction(entry);
  }
  return isFilled(entry.key)
    && (isFilled(entry.label) || typeof entry.label === 'function')
    && entry.paths.length > 0
    && entry.paths.every((child) => isRenderableAction(child) && !isPathGroup(child));
}

/**
 * The creation paths a phone is offered, groups kept and their contents thinned.
 *
 * A path can say it is not a realistic phone workflow and leave rather than being offered and
 * failing. A group emptied that way goes with its children: a named group that expands onto
 * nothing is worse than no group.
 *
 * @param {RecordAction[]} menu
 * @returns {RecordAction[]}
 */
export function compactPaths(menu) {
  return (Array.isArray(menu) ? menu : [])
    .filter((entry) => !entry?.expandedOnly)
    .map((entry) => {
      if (!isPathGroup(entry)) {
        return entry;
      }
      return { ...entry, paths: entry.paths.filter((child) => !child.expandedOnly) };
    })
    .filter((entry) => !isPathGroup(entry) || entry.paths.length > 0);
}

/**
 * Validates the selection-bar actions and reports what a conformance pass would.
 *
 * @param {RecordAction[]} acts
 * @returns {boolean} Whether every entry is renderable; findings are warned either way.
 */
export function validateActs(acts) {
  if (!Array.isArray(acts)) return false;

  const renderable = acts.every(isRenderableAction);
  if (!renderable) {
    reportFinding('an act is missing a key, a label or a handler');
  }

  acts.forEach((action) => {
    if (!['act', 'extract'].includes(action?.kind)) {
      reportFinding(`"${action?.key}" has no kind: does it change records, or only send them out?`);
    }
    if (!SCOPES.includes(action?.scope)) {
      reportFinding(`"${action?.key}" has no scope, so nothing can decide where it renders`);
    }
  });

  const visible = acts.filter((action) => action?.scope !== 'set').length;
  if (visible > ACTION_BUDGETS.act) {
    reportFinding(
      `${visible} visible acts against a budget of ${ACTION_BUDGETS.act}. A page over budget here is `
      + 'usually one doing the work of a bulk-edit surface without having one.',
    );
  }

  return renderable;
}

/**
 * Validates the create control and the paths behind it.
 *
 * @param {RecordAction} create
 * @returns {boolean} Whether the control and every path are renderable; findings are warned either way.
 */
export function validateCreate(create) {
  if (create === null) return true;

  const renderable = isRenderableAction(create);
  if (!renderable) {
    reportFinding('the create control is missing a key, a label or a handler');
  }

  const menu = Array.isArray(create.menu) ? create.menu : [];
  menu.forEach((entry) => {
    if (isRenderableCreatePath(entry)) return;
    if (isPathGroup(entry)) {
      reportFinding(
        `create group "${entry?.key}" needs a label and at least one path, and its paths cannot `
        + 'themselves be groups: one level only',
      );
      return;
    }
    reportFinding(`a create path under "${create.key}" is missing a key, a label or a handler`);
  });

  return renderable;
}

/**
 * @param {RecordAction[]} views
 * @returns {boolean} Whether every entry is renderable; findings are warned either way.
 */
export function validateViews(views) {
  if (!Array.isArray(views)) return false;

  const renderable = views.every(isRenderableAction);
  if (!renderable) {
    reportFinding('a view control is missing a key, a label or a handler');
  }
  if (views.length > ACTION_BUDGETS.view) {
    reportFinding(`${views.length} view controls against a budget of ${ACTION_BUDGETS.view}`);
  }

  return renderable;
}

/**
 * Sorts the acts by where they render. The selection bar takes what waits for rows to be checked,
 * in render order: what changes records, then what only takes them out, so it ends on the safe
 * ones. The header's menu takes what is about the collection itself and needs no selection.
 *
 * @param {RecordAction[]} acts
 * @returns {{ acts: RecordAction[], extracts: RecordAction[], manage: RecordAction[] }}
 */
export function splitActs(acts) {
  const entries = Array.isArray(acts) ? acts.filter(isRenderableAction) : [];

  const inBar = entries.filter((action) => action.scope !== 'set');

  return {
    acts: inBar.filter((action) => action.kind !== 'extract'),
    extracts: inBar.filter((action) => action.kind === 'extract'),
    manage: entries.filter((action) => action.scope === 'set'),
  };
}
